import React, { Fragment, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import config from '../../../config';

import ReportShell from '../../Admin/Reports/shared/ReportShell';
import ReportTable, { formatCount } from '../../Admin/Reports/shared/ReportTable';
import TenantSalesEntryModal from './TenantSalesEntryModal';

const MONTHS = [
    ['01', 'January'], ['02', 'February'], ['03', 'March'], ['04', 'April'],
    ['05', 'May'], ['06', 'June'], ['07', 'July'], ['08', 'August'],
    ['09', 'September'], ['10', 'October'], ['11', 'November'], ['12', 'December'],
];

// DineIn first, then the rest in their original order.
const normalize = (p) => p.replace(/_/g, '').toLowerCase();
const orderPartners = (partners) => [
    ...partners.filter((p) => normalize(p) === 'dinein'),
    ...partners.filter((p) => normalize(p) !== 'dinein'),
];

// Module-level so it isn't recreated each render (which would remount the
// selects and close an open dropdown).
const FieldSelect = ({ t, label, value, onChange, placeholder, options }) => (
    <div className="v3-report-field">
        <span className="v3-label">{t(label)}</span>
        <div className="v3-select-wrap">
            <select className="v3-input v3-select" value={value} onChange={(e) => onChange(e.target.value)}>
                {placeholder && <option value="">{t(placeholder)}</option>}
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    </div>
);

const HomeView = () => {
    const { t } = useTranslation();
    const token = localStorage.getItem('sessiontoken');
    const tenantId = localStorage.getItem('tenantId');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);
    const currentYear = new Date().getFullYear();
    const todayMs = useMemo(() => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); }, []);

    const [type, setType] = useState('daily');
    const [email, setEmail] = useState('');
    const [concessionaireId, setConcessionaireId] = useState('');
    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');
    const [selectedYear, setSelectedYear] = useState(String(currentYear));
    const [selectedMonth, setSelectedMonth] = useState('');
    const [deliveryPartners, setDeliveryPartners] = useState([]);
    const [monthlyData, setMonthlyData] = useState([]);
    const [dailyData, setDailyData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalTx, setModalTx] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const selectedStore = useMemo(
        () => stores.find((s) => s._id === selectedStoreId) || null,
        [stores, selectedStoreId],
    );
    const manualFlag = !!selectedStore?.tenant_manual_flag;

    // Tenant identity → email + concessionaire.
    useEffect(() => {
        axios.get(`${config.hostname}/tenant/${tenantId}`, { headers })
            .then((res) => {
                setEmail(res.data.tenant_email_id);
                setConcessionaireId(res.data.tenant_concessionaire_id?._id || '');
            })
            .catch((err) => console.error('tenant identity load failed', err));
    }, [tenantId, headers]);

    // All stores owned by this tenant login.
    useEffect(() => {
        if (!email || !concessionaireId) return;
        axios.post(`${config.hostname}/tenant/gettenantsbyemailid`, { email_id: email, concessionaire_id: concessionaireId }, { headers })
            .then((res) => setStores(Array.isArray(res.data) ? res.data : []))
            .catch((err) => console.error('tenant stores load failed', err));
    }, [email, concessionaireId, headers]);

    // Delivery partners for the selected store (drives F&B split in manual entry).
    useEffect(() => {
        if (!selectedStoreId) { setDeliveryPartners([]); return; }
        axios.post(`${config.hostname}/tenantdeliverypartner/getdeliverypartner`, { tenant_id: selectedStoreId }, { headers })
            .then((res) => setDeliveryPartners((Array.isArray(res.data) ? res.data : []).map((d) => d.delivery_partner_id.delivery_partner_name)))
            .catch((err) => console.error('delivery partner load failed', err));
    }, [selectedStoreId, headers]);

    const loadMonthly = useMemo(() => () => {
        if (!selectedStoreId || !selectedYear) { setMonthlyData([]); return; }
        setLoading(true);
        axios.post(`${config.hostname}/tenanthome/getmonthlysales`, { tenant_id: selectedStoreId, year: selectedYear }, { headers })
            .then((res) => setMonthlyData(Array.isArray(res.data) ? res.data : []))
            .catch((err) => { console.error('monthly sales load failed', err); setMonthlyData([]); })
            .finally(() => setLoading(false));
    }, [selectedStoreId, selectedYear, headers]);

    const loadDaily = useMemo(() => () => {
        if (!selectedStoreId || !selectedMonth || !selectedYear) { setDailyData([]); return; }
        setLoading(true);
        axios.post(`${config.hostname}/tenanthome/getdailysales`, { tenant_id: selectedStoreId, month: selectedMonth, year: selectedYear }, { headers })
            .then((res) => setDailyData(Array.isArray(res.data) ? res.data : []))
            .catch((err) => { console.error('daily sales load failed', err); setDailyData([]); })
            .finally(() => setLoading(false));
    }, [selectedStoreId, selectedMonth, selectedYear, headers]);

    useEffect(() => { loadMonthly(); }, [loadMonthly]);
    useEffect(() => { loadDaily(); }, [loadDaily]);

    const onTypeChange = (v) => { setType(v); setSelectedMonth(''); setDailyData([]); };
    const onStoreChange = (v) => { setSelectedStoreId(v); setDailyData([]); setMonthlyData([]); };

    const openEntry = (row) => { setModalTx(row); setModalOpen(true); };
    const onSaved = () => { setModalOpen(false); setModalTx(null); loadDaily(); };

    const actionCell = (item) => {
        const hasData = item.transaction_count != null && item.transaction_count !== ''
            && item.total_sales != null && item.total_sales !== '';
        const future = new Date(item.transaction_date).setHours(0, 0, 0, 0) >= todayMs;
        const disabled = hasData || future;
        return (
            <div className="v3-tenant-action">
                <button type="button" className="v3-btn v3-btn-ghost" disabled={disabled} onClick={() => openEntry(item)}>
                    {t('Update')}
                </button>
            </div>
        );
    };

    const columns = useMemo(() => {
        if (type === 'monthly') {
            return [
                { key: 'tenant_name', label: 'Store Name', sortable: false, render: (i) => i.tenant_name || selectedStore?.tenant_store_name || '' },
                { key: 'tenant_code', label: 'Tenant Code', sortable: false, render: (i) => i.tenant_code || selectedStore?.tenant_code || '' },
                { key: 'month', label: 'Month', sortable: false },
                { key: 'transaction_count', label: 'Transaction Count', align: 'right', isCount: true, format: formatCount, sortable: false },
                { key: 'total_sales', label: 'Sales', isMoney: true, align: 'right', sortable: false },
            ];
        }
        const cols = [
            { key: 'tenant_name', label: 'Store Name', sortable: false, render: (i) => i.tenant_name || selectedStore?.tenant_store_name || '' },
            { key: 'tenant_code', label: 'Tenant Code', sortable: false, render: (i) => i.tenant_code || selectedStore?.tenant_code || '' },
            { key: 'transaction_date', label: 'Date', sortable: false },
            { key: 'transaction_count', label: 'Transaction Count', align: 'right', isCount: true, format: formatCount, sortable: false },
            { key: 'total_sales', label: 'Sales', isMoney: true, align: 'right', sortable: false },
        ];
        if (manualFlag) cols.push({ key: '__action', label: 'Action', align: 'right', sortable: false, render: actionCell });
        return cols;
    }, [type, manualFlag, selectedStore, todayMs, t]);

    const rows = type === 'monthly' ? monthlyData : dailyData;
    const emptyMessage = !selectedStoreId
        ? t('Select a store to view its sales.')
        : (type === 'daily' && !selectedMonth)
            ? t('Select a month to view daily sales.')
            : t('No sales found for the selected period.');

    const storeOptions = stores.map((s) => ({
        value: s._id,
        label: `${s.tenant_store_name} - ${s.tenant_code} (${s.tenant_property_id?.property_name || ''})`,
    }));

    return (
        <Fragment>
            <ReportShell
                crumbRoot="Tenant Portal"
                crumb="Sales"
                title={t('My Sales')}
                subtitle={t('View your store sales by month or day' + (manualFlag ? ', and enter sales when a day is missing.' : '.'))}
            >
                <div className="v3-card v3-report-filters">
                    <div className="v3-sadmin-form-grid-3">
                        <FieldSelect t={t} label="Type" value={type} onChange={onTypeChange}
                            options={[{ value: 'daily', label: t('Daily') }, { value: 'monthly', label: t('Monthly') }]} />
                        <FieldSelect t={t} label="Store" value={selectedStoreId} onChange={onStoreChange}
                            placeholder="Select Store" options={storeOptions} />
                        <FieldSelect t={t} label="Year" value={selectedYear} onChange={setSelectedYear}
                            options={[currentYear, currentYear - 1, currentYear - 2].map((y) => ({ value: String(y), label: String(y) }))} />
                        {type === 'daily' && (
                            <FieldSelect t={t} label="Month" value={selectedMonth} onChange={setSelectedMonth}
                                placeholder="Select Month" options={MONTHS.map(([v, l]) => ({ value: v, label: t(l) }))} />
                        )}
                    </div>
                </div>

                <ReportTable
                    columns={columns}
                    rows={rows}
                    loading={loading}
                    emptyMessage={emptyMessage}
                />
            </ReportShell>

            <TenantSalesEntryModal
                open={modalOpen}
                transaction={modalTx}
                deliveryPartners={orderPartners(deliveryPartners)}
                store={{
                    concessionaireCode: selectedStore?.tenant_concessionaire_code,
                    tenantCode: selectedStore?.tenant_code,
                    unitCode: selectedStore?.tenant_unit_code,
                    mallCode: selectedStore?.tenant_mall_code,
                    apiKey: selectedStore?.tenant_api_key,
                    concessionaireId,
                }}
                onClose={() => { setModalOpen(false); setModalTx(null); }}
                onSaved={onSaved}
            />
        </Fragment>
    );
};

export default HomeView;
